import React, { useContext, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Stack,
  Divider,
  Tooltip
} from "@mui/material";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LoginIcon from '@mui/icons-material/Login';
import FlagIcon from "@mui/icons-material/Flag";
import { styled } from "@mui/material/styles";
import Logo from "../../assets/logo.svg";
import LogoAr from "../../assets/logo_ar.svg";
import LogoDark from "../../assets/logo_dark.svg";
import LogoDarkAr from "../../assets/logo_dark_ar.svg";
import { useDispatch, useSelector } from "react-redux";
import { toggleLanguage } from "../../store/i18nSlice";
import { openContactModal } from "../../store/modalSlice";
import { t } from "../../utils/i18n";
import { FaLanguage } from "react-icons/fa6";
import { ThemeContext } from "../../ThemeContext";

// Custom styles for the component
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: "#E6D5FF",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
  borderRadius: "20px",
  padding: "0 20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  maxWidth: "1400px",
  margin: "0 auto",
  height: "60px",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
}));

const NavItem = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: "500",
  color: theme.palette.text.primary,
  fontSize: "16px",
  "&:hover": {
    color: theme.palette.text.secondary,
  },
  "&.MuiButton-root": {
    padding: "10px 16px",
  },
  "&:first-of-type": {
    paddingLeft: "0",
  },
}));

const DemoButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.background.buttonHeader,
  color: "white",
  textTransform: "uppercase",
  fontWeight: "600",
  fontSize: "14px",
  borderRadius: "20px",
  padding: "8px 20px",
  "&:hover": {
    backgroundColor: theme.palette.background.buttonHeaderHover,
  },
}));

const FlagButton = styled(IconButton)(({ theme }) => ({
  marginLeft: "12px",
  padding: "8px",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
  },
}));

const ThemeToggle = styled(IconButton)(({ theme }) => ({
  marginLeft: "12px",
  padding: "8px",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
  },
}));

const Header = () => {
  const { mode, toggleTheme, setMode } = useContext(ThemeContext); // Ensure setMode is available
  const lang = useSelector((state) => state.i18n.lang);
  const dispatch = useDispatch();

  const handleLoginClick = () => {
    window.location.href = "https://cyberplatform.naahel.com/"; 
  };

  useEffect(() => {
    // Load theme from localStorage on component mount
    const storedMode = localStorage.getItem("themeMode");
    if (storedMode) {
      setMode(storedMode); // Use setMode to update the context
    }
  }, [setMode]);

  useEffect(() => {
    // Update localStorage when the theme mode changes
    localStorage.setItem("themeMode", mode);
  }, [mode]);

  const onToggleLanguage = () => {
    dispatch(toggleLanguage());
  };

  const handleOpenModal = () => {
    dispatch(openContactModal(t(lang, "contact.request_offer")));
  };

  return (
    <StyledAppBar
      position="static"
      sx={{
        backgroundColor: (theme) => theme.palette.background.header,
        width: "90%",
        borderRadius: 50,
        marginTop:"30px",
        maxWidth: "90%",
      }}
    >
      <Toolbar sx={{ width: "100%", minHeight: "60px !important" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Stack direction="row" alignItems="center" spacing={5}>
            <LogoContainer onClick={() => console.log("Logo clicked")}>
              <Box>
                <img src={ mode==="light" ? lang==="ar" ? LogoAr : Logo : lang==="ar" ? LogoDarkAr : LogoDark } alt="Naahel Cyber Logo" style={{ height: 30 }} />
              </Box>
            </LogoContainer>

            <Box sx={{ display: "flex", gap: "10px", ml: "20px" }}>
              <NavItem component="a" href="/">
                <Typography variant="h4" gutterBottom fontWeight={500}>
                  {t(lang, "nav.home")}
                </Typography>
              </NavItem>
              <Divider
                sx={{
                  borderRightWidth: 0.2,
                  opacity: 1,
                  height: 20,
                  alignSelf: "center",
                }}
              />
              <NavItem
                onClick={() => {
                  const el = document.getElementById("why-naahelcyber");
                  if (el)
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                <Typography variant="h4" fontWeight={400}>
                  {t(lang, "nav.features")}
                </Typography>
              </NavItem>
              <Divider
                sx={{
                  borderRightWidth: 0.2,
                  opacity: 1,
                  height: 20,
                  alignSelf: "center",
                }}
              />
              <NavItem
                onClick={() => {
                  const el = document.getElementById("pricing");
                  if (el)
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                <Typography variant="h4" fontWeight={400}>
                  {t(lang, "nav.pricing")}
                </Typography>
              </NavItem>
              <Divider
                sx={{
                  borderRightWidth: 0.2,
                  opacity: 1,
                  height: 20,
                  alignSelf: "center",
                }}
              />
              <NavItem
                onClick={() => {
                  const el = document.getElementById("contact-us");
                  if (el)
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                <Typography variant="h4" fontWeight={400}>
                  {t(lang, "nav.contact")}
                </Typography>
              </NavItem>
            </Box>
          </Stack>

          <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <DemoButton
              sx={{ py: 0.5 }}
              variant="contained"
              onClick={handleOpenModal}
            >
              <Typography
                variant="h5"
                fontWeight={500}
                sx={{ wordSpacing: 2, letterSpacing: 3 }}
              >
                {t(lang, "cta.request_demo")}
              </Typography>
            </DemoButton>
            <FlagButton onClick={onToggleLanguage}>
              <FaLanguage />
            </FlagButton>
            <Tooltip title={t(lang, "nav.login_label")} arrow>
            <IconButton
              onClick={handleLoginClick}
              sx={{
                marginLeft: "12px",
                padding: "8px",
                color: (theme) => theme.palette.text.primary,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                },
              }}
              aria-label="login"
            >
              <LoginIcon />
            </IconButton>
            </Tooltip>

            <ThemeToggle
              onClick={() => {
                toggleTheme();
              }}
              sx={{
                width: 32,
                height: 32,
                backgroundColor: "#fff",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "18px",
                fontWeight: "bold",
              }}
            >
              {mode === "dark" ? (
                <LightModeIcon sx={{ color: "#333333ff" }} />
              ) : (
                <DarkModeIcon sx={{ color: "#333333ff" }} />
              )}
            </ThemeToggle>
          </Box>
        </Stack>
      </Toolbar>
    </StyledAppBar>
  );
};

export default Header;
